import { ArrowForwardIcon } from "@chakra-ui/icons";
import { Flex } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { RanaButton, RanaModal, RanaTable } from "@nens/ranalib";
import { listToString } from "@nens/shared-utils";

import { FoldersBreadcrumb } from "@/components/folders-breadcrumb";
import { TableEmptyFiles } from "@/components/table-empty-file";
import { RanaFileIcon, getDisplayType } from "@/lib/rana-file-data-type";
import { cancelButtonText, fileNameColumnString } from "@/translations";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { useProcessRunFormFileModal } from "../hooks/use-process-run-form-file-modal";
import { ProcessRunFormFileModalProps } from "../types";
import { isDirectoryMode } from "../utils/file-modal-mode";

export function SelectFileOrDirectoryModal(props: ProcessRunFormFileModalProps) {
  const { mode, onClose } = props;
  const isDirectory = isDirectoryMode(mode);

  const {
    paths,
    setPaths,
    fileName,
    intl,
    files,
    tableDataIds,
    isLoading,
    onDirectoryOrFileClick,
    saveChanges,
    clearFile,
    getRootProps,
    getInputProps,
    open,
    acceptedDataTypes,
    acceptedFeatureTypes,
    acceptsStudyAreaFile,
    goToDefineStudyArea,
  } = useProcessRunFormFileModal(props);

  const getSelectionButtonTitle = () => {
    if (isDirectory) {
      return intl.formatMessage({
        id: paths.length ? "process.select_directory" : "process.no_directory_selected",
        defaultMessage: paths.length ? "Select folder" : "No folder selected",
      });
    }

    if (fileName) {
      return (
        intl.formatMessage({
          id: "process.select",
          defaultMessage: "Select",
        }) + fileName
      );
    }

    return intl.formatMessage({
      id: "process.no_file_selected",
      defaultMessage: "No file selected",
    });
  };

  const getIncompatibleFileTitle = (isNotAccepted: boolean) => {
    if (!isNotAccepted) return undefined;

    const title = intl.formatMessage(
      {
        id: "file.not_correct_data_type",
        defaultMessage:
          "This file is not compatible with this process. It should be of type: {fileTypes}.",
      },
      {
        fileTypes: listToString(
          acceptedFeatureTypes.length ? acceptedFeatureTypes : acceptedDataTypes,
          intl.formatMessage({
            id: "or",
            defaultMessage: "or",
          })
        ),
      }
    );

    if (!acceptsStudyAreaFile) return title;

    return (
      title +
      intl.formatMessage({
        id: "file.accepts_study_area_file",
        defaultMessage:
          "\nYou can create a study area file in the define study area process (click the button below)",
      })
    );
  };

  const selectionButtonTitle = getSelectionButtonTitle();

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <FormattedMessage
          id="heading.select_a_file"
          defaultMessage="Select a {types} {isDirectory, select, true {directory} other {file}}"
          values={{
            types: listToString(
              (acceptedFeatureTypes.length ? acceptedFeatureTypes : acceptedDataTypes).map(
                getDisplayType
              ),
              "or"
            ).toLowerCase(),
            isDirectory,
          }}
        />
      }
      maxW="xl"
      bottomChild={
        acceptsStudyAreaFile ? (
          <RanaButton
            role="group"
            variant="fourthiary"
            rightIcon={<ArrowForwardIcon />}
            onClick={goToDefineStudyArea}
          >
            <FormattedMessage
              id="link.define_study_area"
              defaultMessage="Go to define study area"
            />
          </RanaButton>
        ) : undefined
      }
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          onClick: onClose,
        },
        {
          variant: "primary",
          title: selectionButtonTitle,
          isDisabled: isDirectory ? !paths.length : !fileName,
          onClick: () => {
            onClose();
            saveChanges();
          },
          children: intl.formatMessage({
            id: isDirectory ? "process.select_directory" : "process.select",
            defaultMessage: isDirectory ? "Select folder" : "Select",
          }),
        },
      ]}
    >
      <Flex justifyContent="space-between" w="full">
        <FoldersBreadcrumb
          paths={paths}
          setPaths={(paths) => {
            setPaths(paths);
            clearFile();
          }}
          isViewingFolder
        />
      </Flex>

      <RanaTable
        columnDefinitions={[
          {
            width: "table_cell_m",
            title: fileNameColumnString(intl),
          },
        ]}
        tableDataIds={tableDataIds}
        emptyElement={<TableEmptyFiles {...{ open, getInputProps, getRootProps }} />}
        isSkeleton={isLoading}
      >
        <RanaTable.Container mt={4}>
          <RanaTable.Body>
            {files.map((fileOrDirectory) => {
              const { id, dataType, type, meta, descriptor } = fileOrDirectory;
              const name = getNameFromFileId(id);
              const { featureTypeDefinition } = meta ?? {};

              const dataTypeIsNotAccepted =
                !!acceptedDataTypes.length &&
                (!dataType || !acceptedDataTypes.includes(dataType + ""));

              const featureTypeIsNotAccepted =
                !!acceptedFeatureTypes.length &&
                (!featureTypeDefinition || !acceptedFeatureTypes.includes(featureTypeDefinition));

              const isNotAccepted =
                type === "file" &&
                (descriptor?.status.id !== "completed" ||
                  dataTypeIsNotAccepted ||
                  featureTypeIsNotAccepted);

              return (
                <RanaTable.Row
                  isSelected={name === fileName}
                  _hover={{ textDecor: "underline" }}
                  cursor="pointer"
                  onClick={() => {
                    clearFile();
                    onDirectoryOrFileClick(fileOrDirectory);
                  }}
                  key={id}
                  rowId={id}
                  title={getIncompatibleFileTitle(isNotAccepted)}
                  // Directory inputs can navigate through directories, but cannot select files.
                  isDisabled={isDirectory ? type !== "directory" : isNotAccepted}
                >
                  <RanaTable.Cell title={name}>
                    <RanaFileIcon fileOrDirectory={fileOrDirectory} />
                    {name}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </RanaModal>
  );
}
